import type { SupplyKind } from "@frontier/content/supplies";
import { useEffect, useState, type CSSProperties } from "react";
import type { SupplyControls } from "@frontier/protocol/supplies";
import "./supplies.css";
import { TRAINING_GATE_FACTIONS } from "@frontier/content/training";
import { canDockAt, nearbyStation } from "@frontier/content/stations";
import type { MissionObjective } from "@frontier/content/contracts";
import type {
  PublicEventsView,
  PublicEventView,
} from "@frontier/content/events";
import { EventControls } from "./EventControls";
import { eventWorkAvailable } from "./event-navigation";
import type { RiskPrivateView } from "@frontier/content/risk";
import { attackableTargets, physicalRegion } from "./targeting";
import { deathMessage } from "./risk-ui";
import { pilotLabel } from "./pilot-label";
import { canLeaveTraining, tutorialCount, tutorialMilestones, tutorialStage } from "@frontier/content/tutorial";
import { moduleIdSchema } from "@frontier/protocol/contracts";
import type {
  ClientAction,
  ContractId,
  MissionView,
  PartyCommand,
  PartyView,
  Profile,
  ShipSnapshot,
  WorldSnapshot,
} from "@frontier/protocol/contracts";
import { Radar } from "./Radar";
import { MissionJournal } from "./MissionJournal";
import { GalaxyMapButton } from "./GalaxyMapButton";
import { moduleLabels } from "./ui-labels";
import { abilityHotkey } from "./ability-controls";
import { PartyControls } from "./PartyControls";
import { ChatButton } from "./ChatButton";
import { pressHandlers } from "./press-handlers";
import { tutorialGuidance, tutorialThreat } from "./onboarding-ui";
import { CargoControl } from "./CargoControl";
import { AbilityIcon } from "./AbilityIcon";
import { AbilityCooldown } from "./AbilityCooldown";
import { availableSurvey } from "./flight-interaction";
import { useBoostHold } from "./use-boost-hold";
import { FlightNavigation } from "./FlightNavigation";
import type { NavigationState } from "./game/navigation-types";
import type { Point } from "./game/geometry";
import "./flight-navigation.css";
import "./autopilot-layout.css";
import "./onboarding.css";
import { FullscreenButton } from "./FullscreenButton";
import { AccountButton } from "./AccountButton";
import { HelpButton } from "./HelpButton";

interface Props {
  supplyControls?: SupplyControls | null;
  onSupply?: (kind?: SupplyKind, targetId?: string) => void;
  cargoOpen: boolean;
  onCargoOpenChange: (open: boolean) => void;
  navigation: NavigationState;
  onNavigate: (point: Point) => void;
  onCancelNavigation: () => void;
  risk: RiskPrivateView | null;
  events: PublicEventsView;
  event: PublicEventView | null;
  eventObjective: MissionObjective | null;
  onJoinEvent: (id: string) => void;
  world: WorldSnapshot;
  pilot: ShipSnapshot;
  profile: Profile;
  mission: MissionView | null;
  missions: readonly MissionView[];
  onFocusContract: (id: ContractId) => void;
  party: PartyView;
  onParty: (command: PartyCommand) => void;
  status: string;
  targetId: string;
  boost: boolean;
  controlsBlocked?: boolean;
  onBoost: (pressed: boolean) => void;
  onAbility: (slot: number) => void;
  onInteract: () => void;
  onAction: (
    type: ClientAction["type"],
    slot?: number,
    targetId?: string,
  ) => void;
  onTarget: (id: string) => void;
  onAccount: () => void;
  onHelp?: () => void;
  onChat: () => void;
  chatUnread: number;
  onOpenGalaxy: () => void;
  hideFullscreen?: boolean;
}

export function Hud({
  supplyControls,
  onSupply,
  cargoOpen,
  onCargoOpenChange,
  navigation,
  onNavigate,
  onCancelNavigation,
  risk,
  events,
  event,
  eventObjective,
  onJoinEvent,
  world,
  pilot,
  profile,
  mission,
  missions,
  onFocusContract,
  party,
  onParty,
  status,
  targetId,
  boost,
  controlsBlocked = false,
  onBoost,
  onAbility,
  onInteract,
  onAction,
  onTarget,
  onAccount,
  onHelp,
  onChat,
  chatUnread,
  onOpenGalaxy,
  hideFullscreen = false,
}: Props) {
  const [openPanel, setOpenPanel] = useState<"journal" | "events" | null>(null);
  useEffect(() => setOpenPanel(null), [profile.runId]);
  const station = nearbyStation(pilot);
  const loot = world.loot.filter(
    (item) => !item.pending && !item.claimedBy.includes(pilot.id),
  );
  const canPickup = loot.some(
    (item) => Math.hypot(item.x - pilot.x, item.y - pilot.y) <= 125,
  );
  const work = eventWorkAvailable(world, pilot, events);
  const workProgress =
    events.participation.find((item) => item.eventId === work?.id)
      ?.interactionProgress ?? 0;
  const canDock = station !== undefined && canDockAt(station, pilot.factionId);
  const survey = availableSurvey(missions, pilot);
  const gate = world.gates.find(
    (item) => Math.hypot(item.x - pilot.x, item.y - pilot.y) <= item.radius,
  );
  const region = world.regions.find(
    (item) =>
      pilot.x >= item.x &&
      pilot.x < item.x + item.width &&
      pilot.y >= item.y &&
      pilot.y < item.y + item.height,
  );
  const enemies = attackableTargets(
    world,
    pilot,
    party.party?.members.map((member) => member.playerId) ?? [],
  ).sort(
    (a, b) =>
      Number(b.id === targetId) - Number(a.id === targetId) ||
      Math.hypot(a.x - pilot.x, a.y - pilot.y) -
        Math.hypot(b.x - pilot.x, b.y - pilot.y),
  );
  const target = enemies.find((item) => item.id === targetId);
  const primaryId = moduleIdSchema.parse(pilot.moduleId);
  const secondaryId = moduleIdSchema.parse(pilot.secondaryModuleId);
  const tutorial = profile.docked || pilot.docked ? null : profile.tutorial;
  const defenseWarning = world.npcs.some(
    (npc) =>
      tutorialThreat(npc, pilot.id) &&
      Math.hypot(npc.x - pilot.x, npc.y - pilot.y) < 750,
  );
  const guidance = tutorialGuidance(
    tutorial,
    defenseWarning,
    primaryId,
    secondaryId,
    physicalRegion(pilot),
  );
  const cellAbilities = pilot.controls?.format === 3 || pilot.controls?.format === 4 ? pilot.controls.abilities : null;
  const manyAbilities = cellAbilities !== null && cellAbilities.length >= 3;
  const deckStyle: CSSProperties & { "--ability-rows": number; "--ability-wide-rows": number } = {
    "--ability-rows": Math.ceil(((cellAbilities?.length ?? 0) + 1) / 3),
    "--ability-wide-rows": Math.ceil(((cellAbilities?.length ?? 0) + 1) / 6),
  };
  const primaryCost = pilot.controls?.format === 1 || pilot.controls?.format === 2 ? pilot.controls.abilityEnergyCosts[0] : undefined;
  const secondaryCost = pilot.controls?.format === 1 || pilot.controls?.format === 2 ? pilot.controls.abilityEnergyCosts[1] : undefined;
  const boostHandlers = useBoostHold(controlsBlocked || pilot.dead || !pilot.controls, onBoost);
  return (
    <div className={`hud${manyAbilities ? " hud-many-abilities" : ""}`} style={deckStyle}>
      <nav className="flight-header" aria-label="Меню полёта">
        <div className="header-right">
          <GalaxyMapButton onOpen={onOpenGalaxy} />
          <MissionJournal
            open={openPanel === "journal"}
            onOpenChange={open => setOpenPanel(current => open ? "journal" : current === "journal" ? null : current)}
            mission={mission}
            missions={missions}
            onFocusContract={onFocusContract}
            guidance={guidance}
            event={event}
            eventObjective={eventObjective}
            eventPresence={
              events.participation.find((value) => value.eventId === event?.id)
                ?.tasks
            }
          />
          <EventControls
            expansion={{
              open: openPanel === "events",
              onChange: open => setOpenPanel(current => open ? "events" : current === "events" ? null : current),
            }}
            navigationTracked={eventObjective !== null}
            world={world}
            view={events}
            profile={profile}
            connected={status === "connected"}
            onJoin={onJoinEvent}
          />
          <PartyControls
            view={party}
            world={world}
            playerId={pilot.id}
            targetId={targetId}
            connected={status === "connected"}
            onCommand={onParty}
          />
          {onHelp ? <HelpButton onOpen={onHelp} /> : !hideFullscreen && <FullscreenButton />}
          <ChatButton unread={chatUnread} onOpen={onChat} />
          <AccountButton factionId={profile.factionId} onOpen={onAccount} />
        </div>
      </nav>

      {guidance && tutorial && (
        <section
          className={`flight-tutorial${guidance.alert ? " alert" : ""}`}
          aria-label="Текущий шаг обучения"
          role="status"
          aria-atomic="true"
        >
          <div className="flight-tutorial-heading">
            <b>ОБУЧЕНИЕ</b>
            <span>
              Подтверждено: {tutorialCount(tutorial)}/
              {tutorialMilestones(tutorial).length}
            </span>
          </div>
          <p>{guidance.message}</p>
        </section>
      )}

      <div className="flight-status">
        <section
          className="ship-vitals-accessible"
          aria-label="Состояние корабля"
        >
          <span>
            Корпус: {Math.ceil(pilot.hp)} из {Math.ceil(pilot.maxHp)}.
          </span>
          <span>
            Щит: {Math.ceil(pilot.shield)} из {Math.ceil(pilot.maxShield)}.
          </span>
          <span>
            Энергия: {Math.ceil(pilot.energy)} из {Math.ceil(pilot.maxEnergy)}.
          </span>
        </section>
        <div className="flight-navigation-anchor">
          <FlightNavigation
            navigation={navigation}
            pilot={pilot}
            onCancel={onCancelNavigation}
          />
        </div>
      </div>

      <Radar
        navigation={navigation}
        onNavigate={onNavigate}
        trackedEventId={eventObjective ? events.trackedEventId : null}
        world={world}
        pilot={pilot}
        mission={mission}
        partyMarker={party.party?.marker ?? null}
        friends={party.party?.members.map((member) => member.playerId) ?? []}
        trainingExitReady={tutorial !== null && canLeaveTraining(tutorial)}
      />

      <CargoControl
        open={cargoOpen}
        onOpenChange={onCargoOpenChange}
        disabled={status !== "connected" || !pilot.lossDisposition}
        pilot={pilot}
        cargo={risk?.cargo}
        contested={pilot.lossDisposition === "drop"}
        onDropContraband={() => onAction("drop", undefined, "contraband")}
        onDropEnergy={() => onAction("drop", undefined, "energy")}
        onDropSalvage={() => onAction("drop", undefined, "salvage")}
        onDropBattery={() => onAction("drop", undefined, "battery")}
        onDropRocket={() => onAction("drop", undefined, "rocket")}
        onDropDrone={() => onAction("drop", undefined, "drone")}
      />

      <div className="target-selector" aria-label="Выбор цели">
        {enemies.slice(0, 2).map((enemy) => (
          <button
            key={enemy.id}
            className={
              enemy.id === targetId ? "target-chip selected" : "target-chip"
            }
            {...pressHandlers(() => onTarget(enemy.id))}
          >
            <span className="target-crosshair">⌖</span>
            <span className="target-name">
              {pilotLabel(enemy.name, enemy.id)}
            </span>
            <small>
              {Math.round(Math.hypot(enemy.x - pilot.x, enemy.y - pilot.y))}м
            </small>
          </button>
        ))}
        {targetId && <button className="target-clear" aria-label="Снять захват цели" title="Снять захват цели · Esc"
          {...pressHandlers(() => onTarget(""))}>×</button>}
        {target && (
          <div className="target-shield">
            <span>{Math.ceil(target.hp + target.shield)}</span>
            <i
              style={{
                width: `${(100 * (target.hp + target.shield)) / (target.maxHp + target.maxShield)}%`,
              }}
            />
          </div>
        )}
      </div>

      {(pilot.pending || canDock || survey || work || canPickup || gate) && (
        <div className="context-action">
          <button
            className="context-button available"
            data-tutorial-highlight={tutorial?.version === 2 && (
              ((tutorialStage(tutorial) === "stationVisited" || tutorialStage(tutorial) === "contractDelivered") && canDock) ||
              (tutorialStage(tutorial) === "cargoCollected" && canPickup) ||
              (tutorialStage(tutorial) === "return" && gate && TRAINING_GATE_FACTIONS[gate.id])
            ) || undefined}
            disabled={
              pilot.pending ||
              pilot.dead ||
              controlsBlocked ||
              (Boolean(work) &&
                (workProgress > 0 || work?.workBlocked) &&
                !canDock &&
                !survey)
            }
            {...pressHandlers(onInteract)}
          >
            <span>
              {pilot.pending
                ? "◌"
                : canDock
                  ? "⌂"
                  : survey
                    ? "⌖"
                    : work
                      ? "⚒"
                      : canPickup
                        ? "↓"
                        : "⌁"}
            </span>
            <b>
              {pilot.pending ? (
                "ПОДТВЕРЖДЕНИЕ"
              ) : canDock ? (
                "СТЫКОВКА"
              ) : survey ? (
                "ИССЛЕДОВАТЬ МАЯК"
              ) : work ? (
                work.workBlocked ? (
                  "ОХРАНА МЕШАЕТ РАЗБОРУ"
                ) : workProgress > 0 ? (
                  `РАЗБОР ${Math.round(workProgress * 100)}%`
                ) : (
                  "РАЗОБРАТЬ ОБЛОМКИ"
                )
              ) : canPickup ? (
                "ЗАБРАТЬ ГРУЗ"
              ) : (
                <>
                  {gate && TRAINING_GATE_FACTIONS[gate.id] && (!profile.tutorial || !canLeaveTraining(profile.tutorial)) ? "ЗАВЕРШИ ОБУЧЕНИЕ" : gate?.name}
                  {gate && TRAINING_GATE_FACTIONS[gate.id] && <small>ВЫБОР ФРАКЦИИ ПОСТОЯННЫЙ · ПЕРЕХОД НА БАЗУ</small>}
                </>
              )}
            </b>
            <kbd>E</kbd>
          </button>
        </div>
      )}

      {supplyControls && supplyControls.runId === profile.runId && supplyControls.hullId === profile.hullId && supplyControls.hullRevision === profile.hullRevision && <div className="utility-deck" aria-label="Расходники">
        {supplyControls.items.filter(item => item.count > 0).map(item => <button key={item.kind}
          disabled={controlsBlocked || pilot.dead || pilot.pending || supplyControls.pending || item.cooldownSeconds > 0 || (item.kind === "battery" && pilot.energy >= pilot.maxEnergy)}
          {...pressHandlers(() => onSupply?.(item.kind, targetId))} title={item.kind === "battery" ? "Батарея · R" : item.kind === "rocket" ? "Ракета · F" : "Ремонтный дрон · T"}>
          <span>{item.kind === "battery" ? "ϟ" : item.kind === "rocket" ? "➤" : "✚"}</span><b>×{item.count}</b>
          <small>{item.cooldownSeconds > 0 ? `${item.cooldownSeconds}с` : item.kind === "battery" ? "R" : item.kind === "rocket" ? "F" : "T"}</small>
        </button>)}
      </div>}
      <div className={`ability-deck${manyAbilities ? " ability-deck-many" : ""}`}>
        {pilot.controls?.format === 2 && pilot.controls.thirdModuleId && <button className="ability tertiary"
          disabled={controlsBlocked || pilot.dead || pilot.pending || (pilot.thirdCooldown ?? 0) > 0 || pilot.energy < (pilot.controls.abilityEnergyCosts[2] ?? Infinity)}
          {...pressHandlers(() => onAbility(2))}><AbilityCooldown seconds={pilot.thirdCooldown ?? 0} /><AbilityIcon kind={pilot.controls.thirdModuleId} /><b>{moduleLabels[pilot.controls.thirdModuleId]}</b>
          <small>{(pilot.thirdCooldown ?? 0) > 0 ? `${Math.ceil(pilot.thirdCooldown ?? 0)} с` : `${pilot.controls.abilityEnergyCosts[2]} ЭН`}</small><kbd>3</kbd></button>}



        <button
          className={`ability boost ${boost ? "pressed" : ""}`}
          disabled={controlsBlocked || pilot.dead || !pilot.controls}
          aria-label="Форсаж — удерживать"
          {...boostHandlers}
        >
          <AbilityIcon kind="boost" />
          <b>ФОРСАЖ</b>
          <kbd>SPACE</kbd>
        </button>
        {cellAbilities ? cellAbilities.map((ability, index) => {
          const cooldown = pilot.abilityCooldowns?.[ability.slot] ?? 0;
          return <button key={ability.slot} className={`ability ${index === 0 ? "primary" : "secondary"}${guidance?.alert && ability.id === "barrier" ? " tutorial-alert" : ""}${tutorial?.version === 2 && tutorialStage(tutorial) === "moduleUsed" && ability.id === "volley" ? " tutorial-highlight" : ""}`}
            disabled={controlsBlocked || pilot.dead || pilot.pending || cooldown > 0 || pilot.energy < ability.energyCost}
            {...pressHandlers(() => onAbility(ability.slot))}>
            <AbilityCooldown seconds={cooldown} />
            <AbilityIcon kind={ability.id} /><b>{moduleLabels[ability.id]}</b>
            <small>{cooldown > 0 ? `${Math.ceil(cooldown)} с` : `${ability.energyCost} ЭН`}</small>
            <kbd>{abilityHotkey(index)}</kbd>
          </button>;
        }) : <>
        <button
          className={`ability primary ${guidance?.alert && primaryId === "barrier" ? "tutorial-alert" : ""}`}
          disabled={
            pilot.dead ||
            pilot.pending ||
            controlsBlocked ||
            pilot.abilityCooldown > 0 ||
            primaryCost === undefined ||
            pilot.energy < primaryCost
          }
          {...pressHandlers(() => onAbility(0))}
        >
          <AbilityCooldown seconds={pilot.abilityCooldown} />
          <AbilityIcon kind={primaryId} />
          <b>{moduleLabels[primaryId]}</b>
          <small>
            {pilot.abilityCooldown > 0
              ? `${Math.ceil(pilot.abilityCooldown)} с`
              : primaryCost === undefined ? "…" : `${primaryCost} ЭН`}
          </small>
          <kbd>Q</kbd>
        </button>
        <button
          className={`ability secondary ${guidance?.alert && secondaryId === "barrier" ? "tutorial-alert" : ""}`}
          disabled={
            pilot.dead ||
            pilot.pending ||
            controlsBlocked ||
            pilot.secondaryCooldown > 0 ||
            secondaryCost === undefined ||
            pilot.energy < secondaryCost
          }
          {...pressHandlers(() => onAbility(1))}
        >
          <AbilityCooldown seconds={pilot.secondaryCooldown} />
          <AbilityIcon kind={secondaryId} />
          <b>{moduleLabels[secondaryId]}</b>
          <small>
            {pilot.secondaryCooldown > 0
              ? `${Math.ceil(pilot.secondaryCooldown)} с`
              : secondaryCost === undefined ? "…" : `${secondaryCost} ЭН`}
          </small>
          <kbd>2</kbd>
        </button>        </>}

      </div>
      {pilot.dead && (
        <div className="recovery">
          <span>АВАРИЙНОЕ ВОССТАНОВЛЕНИЕ</span>
          <b>{deathMessage(risk, pilot)}</b>
        </div>
      )}
      {pilot.hp / pilot.maxHp < 0.25 && !pilot.dead && (
        <div className="damage-vignette" />
      )}
    </div>
  );
}
